import { useState, useEffect, useRef } from "react";
import { fetchAttorneyTrademarks } from "../services/rateCardService";

/**
 * Attorney numbers, and the trademark count they add up to.
 *
 * A firm quotes on the marks it actually holds, and nobody counts those by hand
 * — so the numbers are entered one at a time and the registry is asked what each
 * one carries. The total lands in the Trademarks field, which stays editable:
 * this fills it in, it does not take it over.
 */

const DEBOUNCE_MS = 400;

const chip = {
  display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 8px 5px 12px",
  borderRadius: 999, border: "1px solid var(--line)", background: "#fff", fontSize: 12.5,
};

const AttorneyNumbers = ({ numbers, onChange, onTotal }) => {
  const [draft, setDraft] = useState("");
  const [counts, setCounts] = useState({});
  /* The API's own figure, kept as it came: a trademark held under two numbers is
     deduped there, so adding the per-number counts up here would overstate it
     and disagree with the field we just filled. */
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  /* Bumped to ask again without touching the list — a number added before it
     was registered, or a call that failed, is worth a second look. */
  const [nonce, setNonce] = useState(0);
  const requestRef = useRef(0);

  const key = numbers.join(",");

  useEffect(() => {
    if (!numbers.length) {
      setCounts({});
      setTotal(0);
      setError("");
      return undefined;
    }
    /* The counter drops a reply that arrives after a newer one, so removing a
       number quickly cannot leave the previous total on screen. */
    const id = ++requestRef.current;
    setLoading(true);
    const timer = setTimeout(() => {
      fetchAttorneyTrademarks(numbers)
        .then(({ total, details }) => {
          if (id !== requestRef.current) return;
          const map = {};
          details.forEach((d) => {
            // `found: false` means the registry knows nothing about it — not zero.
            map[String(d.attorney_number)] = d.found === false ? null : Number(d.trademarks) || 0;
          });
          setCounts(map);
          setTotal(total);
          setError("");
          onTotal(total);
        })
        .catch((err) => {
          if (id !== requestRef.current) return;
          setError(err?.message || "Could not look these up");
        })
        .finally(() => { if (id === requestRef.current) setLoading(false); });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce]);

  const add = () => {
    const value = draft.trim();
    if (!value) return;
    // The same number twice would double the marks it stands for.
    if (numbers.includes(value)) { setDraft(""); return; }
    onChange([...numbers, value]);
    setDraft("");
  };

  const remove = (value) => onChange(numbers.filter((n) => n !== value));

  const resolved = numbers.filter((n) => counts[n] != null);
  const unresolved = numbers.length - resolved.length;

  return (
    <>
      <label>
        Attorney numbers{" "}
        <span style={{ fontWeight: 400, color: "var(--mut2)" }}>
          · optional · trademarks fill in from these
        </span>
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Enter attorney number, press Enter"
          style={{ flex: 1 }}
        />
        <button
          className="btn sec2"
          onClick={add}
          disabled={!draft.trim()}
          style={{ width: "auto", padding: "0 16px", marginTop: 0, opacity: draft.trim() ? 1 : 0.5 }}
        >
          Add
        </button>
        {numbers.length > 0 && (
          <button
            className="btn sec2"
            onClick={() => setNonce((n) => n + 1)}
            disabled={loading}
            title="Look these up again"
            style={{ width: "auto", padding: "0 14px", marginTop: 0, opacity: loading ? 0.5 : 1 }}
          >
            {loading ? "…" : "⟳"}
          </button>
        )}
      </div>

      {numbers.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
          {numbers.map((n) => {
            const count = counts[n];
            return (
              <span key={n} style={chip}>
                <b>{n}</b>
                <span style={{ color: count ? "var(--emr)" : "var(--mut2)" }}>
                  {loading && count === undefined
                    ? "…"
                    : count == null
                      ? "not found"
                      : `${Number(count).toLocaleString("en-IN")} TM`}
                </span>
                <button
                  onClick={() => remove(n)}
                  title={`Remove ${n}`}
                  style={{
                    width: 18, height: 18, borderRadius: 999, border: "none", cursor: "pointer",
                    background: "var(--tint)", color: "var(--ind2)", fontSize: 12, lineHeight: 1,
                    display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0,
                  }}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}

      {numbers.length > 0 && !error && (
        <div style={{ fontSize: 11.5, color: "var(--mut2)", marginTop: 8, lineHeight: 1.5 }}>
          {loading ? (
            "Looking up trademark counts…"
          ) : (
            resolved.length === 0 ? (
              <span style={{ color: "var(--amb)" }}>
                None of these attorney numbers matched anything. Check them, press ⟳ to look
                again, or type the count in below.
              </span>
            ) : (
              <>
                <b style={{ color: "var(--ink)" }}>{total.toLocaleString("en-IN")}</b> trademarks
                from {resolved.length} number{resolved.length === 1 ? "" : "s"}
                {unresolved > 0 && <span style={{ color: "var(--amb)" }}> · {unresolved} not found</span>}.
                {" "}Edit the field below to quote a different count.
              </>
            )
          )}
        </div>
      )}
      {error && (
        <div className="warn">
          {error}{" "}
          <button
            onClick={() => setNonce((n) => n + 1)}
            style={{ border: "none", background: "none", padding: 0, color: "var(--ind)", fontWeight: 700, cursor: "pointer", fontSize: "inherit", width: "auto", marginTop: 0 }}
          >
            Try again
          </button>
        </div>
      )}
    </>
  );
};

export default AttorneyNumbers;
